<template>
  <section class="section-base form-section-base">
    <div class="form-section-base__title">
      Сведения о доверенности в машиночитаемом виде
    </div>

    <v-container>
      <div class="form-base">
        <MrpaUploader
          :v="$v"
          @parsed-mrpa-change="parsedMrpaChangeHandler"
          :statement-type="StatementType.ROD"
          :statement-id="$route.params.statementId"
        />

        <template v-if="formVisible">
          <div class="form-section-base__controls">
            <v-row>
              <v-col>
                <v-radio-group v-model="role" row :disabled="formVisible">
                  <v-radio label="Физическое лицо" :value="Role.PERSON" />
                  <v-radio
                    label="Юридическое лицо"
                    :value="Role.ORGANIZATION"
                  />
                  <v-radio
                    label="Иностранное юридическое лицо"
                    :value="Role.FOREIGN_ORGANIZATION"
                  />
                </v-radio-group>
              </v-col>
            </v-row>
          </div>

          <Person v-if="role === Role.PERSON" :v="$v.applicant.person" />

          <OrganizationFields
            v-if="role === Role.ORGANIZATION"
            :v="$v.applicant.organization"
          />

          <ForeignOrganizationFields
            v-if="role === Role.FOREIGN_ORGANIZATION"
            :v="$v.applicant.foreignOrganization"
          />

          <Trustee
            v-if="trusteeRole === TrusteeRole.TRUSTEE"
            :v="$v.applicant.trustee"
            :label="getTrusteeLabel(TrusteeRole.TRUSTEE)"
          />
          <TrusteeOrganization
            v-if="trusteeRole === TrusteeRole.TRUSTEE_ORGANIZATION"
            :v="$v.applicant.trusteeOrganization"
            :label="getTrusteeLabel(TrusteeRole.TRUSTEE_ORGANIZATION)"
          />
        </template>

        <Footer
          @next-btn-click="btnSaveClickHandler(StepDirection.NEXT)"
          @prev-btn-click="btnSaveClickHandler(StepDirection.PREV)"
          :loading-next="loadingNext"
        />
      </div>
    </v-container>
  </section>
</template>

<script>
import { mdiAlertCircleOutline } from '@mdi/js';

import { Role } from '@/utils/dictionaries';
import { leaveStep, checkInvalid } from '@/utils/services';
import Footer from '@/components/services/elements/Footer';
import { StepDirection, StepName } from '@/types/services';
import { DocumentType } from '@/types/services';
import { getValidationErrorText } from '@/utils/validate';
import Person, { personValidation, personModel } from './Person';
import OrganizationFields, {
  organizationModel,
  organizationValidation,
} from '@/components/services/ROD/StepApplicant/Machine/Organization';
import ForeignOrganizationFields, {
  foreignOrganizationModel,
  foreignOrganizationValidation,
} from '@/components/services/ROD/StepApplicant/Machine/ForeignOrganization';
import cloneDeep from 'clone-deep';
import Trustee, {
  trusteeModel,
  getTrusteeValidation,
} from '@/components/services/ROD/StepApplicant/Machine/Trustee';
import TrusteeOrganization, {
  trusteeOrganizationModel,
  getTrusteeOrganizationValidation,
} from '@/components/services/ROD/StepApplicant/Machine/TrusteeOrganization';
import { ServicesModule } from '@/store/modules/services';
import deepmerge from 'deepmerge';
import { MrpaType, StatementType, TrusteeRole } from '@/constants';
import MrpaUploader, {
  mrpaUploaderModel,
  mrpaUploaderValidation,
  prefillMrpDisabled,
  getMrpa,
} from '@/components/services/elements/MrpaUploader';
import { MrpaSubjectModule } from '@/store/modules/mrpaSubject';
import { assistantModel } from '@/components/services/elements/Assistant';

const getDefaultForm = () => ({
  person: cloneDeep(personModel),
  organization: cloneDeep(organizationModel),
  foreignOrganization: cloneDeep(foreignOrganizationModel),
  trustee: cloneDeep(trusteeModel),
  trusteeOrganization: cloneDeep(trusteeOrganizationModel),
});

export default {
  name: 'Machine',
  props: {
    loadingNext: {
      type: Boolean,
      required: true,
    },
    roleType: {
      type: String,
      required: true,
    },
  },
  components: {
    Person,
    OrganizationFields,
    ForeignOrganizationFields,
    Footer,
    Trustee,
    TrusteeOrganization,
    MrpaUploader,
  },
  data() {
    return {
      mdiAlertCircleOutline,
      getValidationErrorText,
      mrpa: cloneDeep(mrpaUploaderModel.mrpa),
      formVisible: mrpaUploaderModel.formVisible,
      applicant: getDefaultForm(),
      DocumentType,
      StepDirection,
      StatementType,
      StepName,
      leaveStep,
      Role,
      invalidFields: [],
      role: Role.FOREIGN_ORGANIZATION,
      trusteeRole: TrusteeRole.TRUSTEE,
      TrusteeRole,
    };
  },
  validations() {
    return {
      ...mrpaUploaderValidation,
      applicant: {
        person: personValidation,
        organization: organizationValidation,
        foreignOrganization: foreignOrganizationValidation,
        trustee: getTrusteeValidation(this.applicant.trustee),
        trusteeOrganization: getTrusteeOrganizationValidation(
          this.applicant.trusteeOrganization
        ),
      },
    };
  },
  created() {
    const applicantStore = cloneDeep(
      ServicesModule.statement.subjects.applicant
    );

    if (
      applicantStore.person ||
      applicantStore.organization ||
      applicantStore.foreignOrganization
    ) {
      this.prefillDraft(applicantStore, false);
    }
  },
  methods: {
    getTrusteeLabel(trusteeRole) {
      const applicantRoleToName = {
        [Role.PERSON]: 'физического',
        [Role.ORGANIZATION]: 'юридического',
        [Role.FOREIGN_ORGANIZATION]: 'иностранного юридического',
      };
      const trusteeRoleToName = {
        [TrusteeRole.TRUSTEE]: 'физическое',
        [TrusteeRole.TRUSTEE_ORGANIZATION]: 'юридическое',
      };

      return `Сведения о представителе ${
        applicantRoleToName[this.role]
      } лица (${trusteeRoleToName[trusteeRole]} лицо)`;
    },

    prefillDraft(applicant, isMrpa) {
      if (isMrpa) {
        prefillMrpDisabled(applicant);
      }

      if (applicant.person) {
        this.role = Role.PERSON;
        this.applicant.person = deepmerge.all([
          personModel,
          applicant.person,
          {
            identityDocument: {
              ...personModel.identityDocument,
              ...applicant.identityDocument,
            },
          },
        ]);
      }

      if (applicant.organization) {
        this.role = Role.ORGANIZATION;
        this.applicant.organization = deepmerge(
          organizationModel,
          applicant.organization
        );
      }

      if (applicant.foreignOrganization) {
        this.role = Role.FOREIGN_ORGANIZATION;
        this.applicant.foreignOrganization = deepmerge(
          foreignOrganizationModel,
          applicant.foreignOrganization
        );
      }

      if (applicant.trustee) {
        let trustee = deepmerge(trusteeModel, applicant.trustee);

        if (trustee.assistantJob) {
          trustee = deepmerge(assistantModel, trustee);
        }

        this.applicant.trustee = trustee;

        this.mrpa = getMrpa(this.applicant);

        this.trusteeRole = TrusteeRole.TRUSTEE;
      }

      if (applicant.trusteeOrganization) {
        let trusteeOrganization = deepmerge(
          trusteeOrganizationModel,
          applicant.trusteeOrganization
        );

        if (trusteeOrganization.assistantJob) {
          trusteeOrganization = deepmerge(assistantModel, trusteeOrganization);
        }

        this.applicant.trusteeOrganization = trusteeOrganization;

        const {
          file,
          fileSig,
        } = this.applicant.trusteeOrganization.documents.authorityDocument;

        this.prefillDraftMrpa(
          file,
          fileSig,
          this.applicant.trusteeOrganization.baseAuthority.issueNumber,
          this.applicant.trusteeOrganization.baseAuthority.mrpIssuerInn
        );

        this.trusteeRole = TrusteeRole.TRUSTEE_ORGANIZATION;
      }

      if (
        !applicant.person &&
        !applicant.organization &&
        !applicant.foreignOrganization
      ) {
        this.role = Role.PERSON;
        this.applicant.person = cloneDeep(personModel);
      }

      this.formVisible = true;
    },

    prefillDraftMrpa(file, fileSig, number, issuerInn) {
      if (file) {
        this.mrpa.file = file;
        this.mrpa.fileSig = fileSig;
        this.mrpa.type = MrpaType.BY_FILE;
      } else {
        this.mrpa.guid = number;
        this.mrpa.issuerInn = issuerInn;
        this.mrpa.type = MrpaType.BY_GUID;
      }
    },

    assemblyModel() {
      const applicant = {
        mrpType: this.mrpa.type,
      };

      applicant[this.role] = this.applicant[this.role];

      if (this.role === Role.PERSON) {
        applicant.identityDocument = this.applicant.person.identityDocument;
        delete applicant.person.identityDocument;
      }

      applicant[this.trusteeRole] = this.applicant[this.trusteeRole];

      if (this.trusteeRole === TrusteeRole.TRUSTEE_ORGANIZATION) {
        if (this.mrpa.type === MrpaType.BY_GUID) {
          delete applicant.trusteeOrganization.documents.authorityDocument;
        }
      } else {
        if (this.mrpa.type === MrpaType.BY_GUID) {
          delete applicant.trustee.documents.authorityDocument;
        }
      }

      return applicant;
    },

    async touchFields() {
      this.invalidFields = [];

      this.$v.formVisible.$touch();

      if (this.$v.formVisible.$invalid) {
        checkInvalid(this.$v.formVisible, this.invalidFields);
        return;
      }

      this.$v.applicant[this.role].$touch();
      this.$v.applicant[this.trusteeRole].$touch();

      checkInvalid(this.$v.applicant[this.role], this.invalidFields);
      checkInvalid(this.$v.applicant[this.trusteeRole], this.invalidFields);

      if (this.invalidFields.length === 0) {
        if (this.mrpa.type === MrpaType.BY_GUID) {
          const isMrpaValid = await this.checkMrpaGuid();

          if (!isMrpaValid) this.invalidFields.push(true);
        }
      }
    },

    parsedMrpaChangeHandler(parsedMrpa) {
      if (parsedMrpa) {
        this.prefillDraft(parsedMrpa, true);
        this.formVisible = true;
      } else {
        this.role = Role.PERSON;
        this.applicant = getDefaultForm();
        this.formVisible = null;
      }
    },

    async checkMrpaGuid() {
      try {
        await MrpaSubjectModule.checkMrpaSubjectGuid({
          statementId: this.$route.params.statementId,
          guid: this.mrpa.guid,
          issuerInn: this.mrpa.issuerInn,
        });

        return true;
      } catch (e) {
        return false;
      }
    },

    btnSaveClickHandler(direction) {
      leaveStep({
        stepThis: this,
        direction,
        step: StepName.S2,
      });
    },
  },
};
</script>
